import { TypeAsserter, TypeChecker } from '@attain-apps/type-guards'

import * as common from '../../../../../common'
import type { Expression } from '../../expression'
import type { FnHelper } from '../helper'
import { Key } from '../key'
import * as fnNode from '../node'

import { CommonArgs, standardNodeCreator } from './utils'

/**
 * Creates a
 * {@link https://en.wikipedia.org/wiki/Logical_conjunction | logical conjunction}
 *
 * @param expressions List of expressions to conjoin
 * @example
 * {
 *   '{}': {
 *     isActive: { '@a': 'is_active' },
 *     isDirect: { '@a': 'is_direct' },
 *     isActiveAndDirect: { and: [ { '@': 'isActive' }, { '@': 'isDirect' } ] }
 *   }
 * }
 * // ->
 * { isActive: true, isDirect: true, isActiveAndDirect: true },
 * { isActive: true, isDirect: false, isActiveAndDirect: false },
 * { isActive: false, isDirect: true, isActiveAndDirect: false },
 * { isActive: false, isDirect: false, isActiveAndDirect: false },
 * ...
 */
export interface And<out T = any> {
  /** {@link And} */
  readonly [Key.And]: T extends boolean ? readonly Expression<boolean>[] : never
}
export namespace And {
  export const isAnd: TypeChecker<And> = TypeChecker.Factory.lazy(() =>
    TypeChecker.Factory.object<And>({
      [Key.And]: CommonArgs.isListOfExpressions as TypeChecker<And[Key.And]>,
    }),
  ).usingAlias('Fn.And')
  export const assertIsAnd: TypeAsserter<And> = TypeAsserter.Factory.from(isAnd)

  /**
   * @see {@link Node}
   */
  export class Node<T extends boolean = boolean> extends fnNode.Node<
    T,
    Key.And,
    readonly fnNode.Node<boolean>[]
  > {
    public readonly type = common.value.types.Intrinsic.Boolean

    constructor(arg: Node<T>['arg']) {
      super(Key.And, arg)
    }
  }
  export namespace Node {
    export const isNode: TypeChecker<Node> = TypeChecker.Factory.instance(Node)
    export const assertIsNode: TypeAsserter<Node> =
      TypeAsserter.Factory.from(isNode)
  }

  const expressionsInArgs: FnHelper<And>['expressionsInArgs'] = fn =>
    fn[Key.And]
  export const HELPER: FnHelper<And> = {
    createNode: standardNodeCreator.usingExpressionList({
      // Empty conjunctions (or conjunctions with all trues) are true
      emptyValue: true,
      finalizeArgs: (args: Node<boolean>[]) => args,
      getArgExpressions: expressionsInArgs,
      // Any falsy values cause the whole conjunction to be false
      handleValue: node => (!node.value ? { return: false } : undefined),
      nodeClass: Node,
      setupStash: () => undefined,
    }),
    expressionsInArgs,
    typeChecker: isAnd,
  }
}
